import Reveal from "@/components/Reveal";
import HeartButton from "@/components/HeartButton";
import useSwipe from "@/hooks/useSwipe";
import { resolveImage } from "@/lib/adminApi";

// Glide to the neighbouring artwork's DOM position within the hall.
function walkToArtwork(targetIndex1Based) {
  const el = document.querySelector(`[data-testid=artwork-${targetIndex1Based}]`);
  if (!el || !window.__hscroll) return;
  window.__hscroll.toElement(el);
}

// A single work hung on the wall: framed canvas under a spotlight, an
// oversized ghost number behind, and a museum wall-label alongside.
// Click the frame or the title to open the dedicated painting page.
export default function ArtworkSection({ painting, index, total, onOpen }) {
  const num = String(index + 1).padStart(2, "0");
  const oneBased = index + 1;
  const hasPrev = index > 0;
  const hasNext = total ? index < total - 1 : true;
  const email =
    (typeof window !== "undefined" && window.__ARTIST_EMAIL__) ||
    "info@wwabstract.com";
  const open = () => onOpen && onOpen(painting);
  const swipe = useSwipe({
    onLeft: () => hasNext && walkToArtwork(oneBased + 1),
    onRight: () => hasPrev && walkToArtwork(oneBased - 1),
  });

  return (
    <section className="panel panel--artwork" data-testid={`artwork-${oneBased}`}>
      <div className="ghost ghost--num" aria-hidden="true">
        <span>{num}</span>
      </div>

      <div className="artwork__stage" {...swipe} data-testid={`artwork-stage-${oneBased}`}>
        <span className="art-spot" aria-hidden="true" />
        <div className="artwork__hang">
        <figure
          className="frame art-frame"
          data-cursor="Open"
          data-testid={`artwork-image-${oneBased}`}
          onClick={open}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open()}
        >
          <img
            src={
              painting.image_url && painting.image_url.includes("images.unsplash.com")
                ? `${painting.image_url}&w=1400`
                : resolveImage(painting.image_url)
            }
            alt={painting.alt}
            loading="eager"
            decoding="async"
            draggable="false"
          />
          {painting.status_tag && (
            <span className="artwork__sticker" data-testid={`artwork-sticker-${oneBased}`}>
              {painting.status_tag}
            </span>
          )}
        </figure>

        {painting.room_photos && painting.room_photos.length > 0 && (
          <div className="artwork__strip" data-testid={`artwork-strip-${oneBased}`}>
            {painting.room_photos.slice(0, 5).map((u, i) => (
              <button
                key={u}
                type="button"
                className="artwork__strip-thumb"
                onClick={open}
                aria-label={`View room photo ${i + 1} of ${painting.title}`}
                data-testid={`artwork-strip-thumb-${oneBased}-${i + 1}`}
              >
                <img
                  src={resolveImage(u)}
                  alt={`${painting.title} in a room ${i + 1}`}
                  loading="lazy"
                  draggable="false"
                />
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      <div className="artwork__label">
        <div className="artwork__row">
          <span className="artwork__row-spacer" aria-hidden="true" />
          {index === 0 && (
            <span className="artwork__swipe-hint mono-caps" aria-hidden="true" data-testid="swipe-hint">
              Swipe the painting
            </span>
          )}
          <div className="artwork__arrows" role="group" aria-label="Move between paintings">
            <HeartButton slug={painting.slug} title={painting.title} className="heart-btn--hall" testId={`heart-hall-${oneBased}`} />
            <button
              type="button"
              className="artwork__arrow"
              aria-label="Previous painting"
              disabled={!hasPrev}
              onClick={() => hasPrev && walkToArtwork(oneBased - 1)}
              data-testid={`artwork-prev-${oneBased}`}
            >
              &larr;
            </button>
            <button
              type="button"
              className="artwork__arrow"
              aria-label="Next painting"
              disabled={!hasNext}
              onClick={() => hasNext && walkToArtwork(oneBased + 1)}
              data-testid={`artwork-next-${oneBased}`}
            >
              &rarr;
            </button>
          </div>
        </div>

        <Reveal as="h2" className="artwork__title" delay={0.06} data-testid={`artwork-title-${oneBased}`}>
          <button type="button" className="artwork__title-btn" onClick={open} data-testid={`artwork-open-${oneBased}`}>
            {painting.title}
          </button>
        </Reveal>
        <Reveal as="div" className="artwork__spec mono-caps" delay={0.12}>
          <span>{painting.medium}</span>
          <span className="artwork__spec-sep">/</span>
          <span>{painting.dimensions_display || painting.dimensions}</span>
        </Reveal>
        {painting.price_display && (
          <Reveal as="p" className="artwork__price mono-caps" delay={0.15} data-testid={`artwork-price-${oneBased}`}>
            {painting.price_display}
          </Reveal>
        )}
        <Reveal as="p" className="artwork__story" delay={0.18} data-testid={`artwork-story-${oneBased}`}>
          {painting.story}
        </Reveal>
        <Reveal as="div" className="artwork__foot" delay={0.24}>
          <a
            className="artwork__contact mono-caps"
            href={`mailto:${email}?subject=${encodeURIComponent("Inquiry — " + painting.title)}`}
            data-cursor="Write"
            data-testid={`artwork-contact-${oneBased}`}
          >
            Contact the artist
          </a>
        </Reveal>
      </div>
    </section>
  );
}
